
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function News() {
  const newsItems: any[] = [];

  const getColorClasses = (color: string) => {
    const colorMap = {
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getIconColorClasses = (color: string) => {
    const colorMap = {
      red: 'text-red-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      purple: 'text-purple-600'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* ヒーローセクション */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://public.readdy.ai/ai/img_res/8d08f9738ee92f589a7bdb538a7a6343.jpg')`
        }}
      >
        <div className="text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">お知らせ</h1>
          <p className="text-xl text-blue-200">News</p>
        </div>
      </section>

      {/* お知らせ一覧 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              最新のお知らせ
            </h2>
            <p className="text-xl text-gray-600">
              SUNFIELDからの重要なお知らせや最新情報をお届けします
            </p>
          </div>

          {newsItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-list-line text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-3">お知らせはありません</h3>
              <p className="text-gray-500">現在、表示できるお知らせがありません。</p>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {newsItems.map((news) => (
                  <article key={news.id} className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(news.color)}`}>
                            <i className={`${news.icon} text-xl ${getIconColorClasses(news.color)}`}></i>
                          </div>
                          <div>
                            <div className="flex items-center space-x-3 mb-1">
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getColorClasses(news.color)}`}>
                                {news.category}
                              </span>
                              {news.isNew && (
                                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500 text-sm">{news.date}</p>
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold text-blue-900 mb-4">
                        {news.title}
                      </h3>
                      
                      <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {news.content}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* ページネーション */}
              <div className="flex justify-center mt-12">
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <button className="px-4 py-2 rounded-lg bg-blue-900 text-white font-semibold cursor-pointer">
                    1
                  </button>
                  <button className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    2
                  </button>
                  <button className="px-3 py-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* カテゴリ別お知らせ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              カテゴリ別お知らせ
            </h2>
            <p className="text-xl text-gray-600">
              お探しの情報をカテゴリから見つけてください
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                category: '重要なお知らせ',
                count: 0,
                icon: 'ri-error-warning-line',
                color: 'red',
                description: '営業日程や重要な変更事項'
              },
              {
                category: 'サービス情報',
                count: 0,
                icon: 'ri-service-line',
                color: 'blue',
                description: '新サービスや機能追加'
              },
              {
                category: '採用情報',
                count: 0,
                icon: 'ri-user-add-line',
                color: 'green',
                description: '求人募集や採用関連'
              },
              {
                category: '安全への取り組み',
                count: 0,
                icon: 'ri-shield-check-line',
                color: 'yellow',
                description: '安全管理や講習会情報'
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${getColorClasses(category.color)}`}>
                  <i className={`${category.icon} text-2xl ${getIconColorClasses(category.color)}`}></i>
                </div>
                <h3 className="text-lg font-bold text-blue-900 mb-2">{category.category}</h3>
                <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                <div className="text-2xl font-bold text-blue-700 mb-2">{category.count}</div>
                <p className="text-gray-500 text-xs">件のお知らせ</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* メール配信登録 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
            最新情報をメールで受け取る
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            重要なお知らせや最新情報をメールでお届けします。ぜひご登録ください。
          </p>
          
          <div className="max-w-md mx-auto">
            <div className="flex">
              <input
                type="email"
                placeholder="メールアドレスを入力"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button className="bg-blue-900 text-white px-6 py-3 rounded-r-lg font-semibold hover:bg-blue-800 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                登録
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              ※配信停止はいつでも可能です
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ご質問やお問い合わせはこちら
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            お知らせに関するご質問や、その他のお問い合わせもお気軽にご連絡ください。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせ
            </Link>
            <a
              href="tel:090-9801-4659"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              090-9801-4659
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
